/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Globe, Users, ChartLine } from "@phosphor-icons/react";
import { toast } from "sonner";

// COSMIC-COBALT THEME — distinct from Lottery (vault green) and Revenue (gold).
const TH = {
  bg:        "#070912",
  surface:   "#101428",
  surfaceHi: "#191F3A",
  border:    "#252C4D",
  borderHi:  "#3F4A7A",
  accent:    "#7BB4FF",   // cosmic blue
  accentDim: "#3B6BB3",
  warm:      "#FFB155",   // accent for non-India bars
  pop:       "#F0C75E",
  text:      "#EDF1FB",
  textMute:  "#8E99C2",
  textDim:   "#56608A",
};

const PERIODS = [
  { key: "today", label: "Last 24h" },
  { key: "7d",    label: "Last 7 days" },
  { key: "30d",   label: "Last 30 days" },
  { key: "ytd",   label: "Year-to-Date" },
  { key: "all",   label: "All-time" },
];

export default function DemographicsAnalyticsPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [activePeriod, setActivePeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Super-admin gate
  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_super_admin) {
      toast.error("Demographics analytics is a Super-Admin tool.");
      navigate("/", { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user?.is_super_admin) return;
    setLoading(true);
    api.get("/admin/analytics/demographics")
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load demographics."))
      .finally(() => setLoading(false));
  }, [user]);

  const bucket = data?.periods?.[activePeriod];

  return (
    <div className="min-h-screen" style={{ backgroundColor: TH.bg, color: TH.text }}>
      <Header />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14">
        <button
          type="button" onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] mb-7 transition-opacity hover:opacity-100"
          style={{ color: TH.textMute, opacity: 0.7 }}
          data-testid="demo-back"
        >
          <ArrowLeft size={12} weight="bold" /> Back
        </button>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.25em] mb-5"
          style={{ backgroundColor: `${TH.accent}1A`, color: TH.accent, border: `1px solid ${TH.accentDim}` }}>
          <Globe size={11} weight="bold" /> Super-Admin · Internal Tool
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
          User <span className="italic" style={{ color: TH.accent }}>Demographics</span>
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed font-light" style={{ color: TH.textMute }}>
          Country-level distribution of <b style={{ color: TH.text }}>active users</b> (visitors with a live session)
          across last 24h, week, month, YTD and all-time — calibrated against the same <b>last_seen</b>
          signal that powers the Active-Online tool. Anonymous visitors are counted but kept separate from
          the country breakdown, which only covers signed-in users (where we have geo from kundali onboarding).
        </p>

        {/* Period tabs */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="demo-period-tabs">
          {PERIODS.map((p) => (
            <button
              key={p.key} type="button" onClick={() => setActivePeriod(p.key)}
              className="px-4 py-2 rounded-full font-mono text-xs uppercase tracking-wider transition-all"
              style={{
                backgroundColor: activePeriod === p.key ? TH.accent : "transparent",
                color: activePeriod === p.key ? TH.bg : TH.textMute,
                border: `1px solid ${activePeriod === p.key ? TH.accent : TH.border}`,
                fontWeight: activePeriod === p.key ? 700 : 400,
              }}
              data-testid={`demo-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="mt-10 text-center font-mono text-sm" style={{ color: TH.textDim }}>
            Computing demographics…
          </div>
        )}

        {!loading && data && bucket && (
          <Snapshot data={data} bucket={bucket} period={activePeriod} />
        )}
      </main>
      <Footer />
    </div>
  );
}

// =============================================================================
const Snapshot = ({ data, bucket, period }) => {
  return (
    <div className="mt-8 space-y-8" data-testid="demo-snapshot">
      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPI label="Active users" value={bucket.active_users}
          hint="Distinct visitors (signed-in + anonymous)" icon={<Users size={16} weight="duotone" />} />
        <KPI label="Signed-in" value={bucket.signed_in}
          hint={`${bucket.active_users ? Math.round((bucket.signed_in / bucket.active_users) * 100) : 0}% of active`}
          icon={<Users size={16} weight="fill" />} />
        <KPI label="Anonymous" value={bucket.anonymous}
          hint="Visited without signing in" icon={<Users size={16} weight="light" />} />
        <KPI label="Unique countries" value={bucket.unique_countries}
          hint="Among signed-in users" icon={<Globe size={16} weight="duotone" />} />
      </div>

      {/* Country breakdown */}
      <CountryTable bucket={bucket} period={period} />

      {/* 30-day active-users timeline */}
      {period === "30d" && data.timeline_last_30d?.length > 0 && (
        <Timeline rows={data.timeline_last_30d} />
      )}
    </div>
  );
};

const KPI = ({ label, value, hint, icon }) => (
  <div className="rounded-xl p-5" style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}` }}
    data-testid="demo-kpi">
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: TH.textMute }}>
      {icon} {label}
    </div>
    <div className="mt-2 text-3xl font-light" style={{ color: TH.accent }}>{value}</div>
    <div className="mt-1 text-xs font-light" style={{ color: TH.textDim }}>{hint}</div>
  </div>
);

const CountryTable = ({ bucket, period }) => {
  const rows = bucket.countries || [];
  if (rows.length === 0) {
    return (
      <div className="rounded-xl p-8 text-center font-mono text-sm"
        style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}`, color: TH.textDim }}>
        No signups in this period yet.
      </div>
    );
  }
  const maxUsers = Math.max(...rows.map((r) => r.users));
  return (
    <div className="rounded-xl p-5" style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}` }}
      data-testid={`demo-country-table-${period}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: TH.textMute }}>
          <ChartLine size={12} weight="bold" /> Country breakdown · {bucket.label}
        </div>
        <div className="font-mono text-[10px]" style={{ color: TH.textDim }}>
          {rows.length} {rows.length === 1 ? "country" : "countries"}
        </div>
      </div>
      <ul className="space-y-2.5">
        {rows.map((r, idx) => (
          <li key={r.country} className="grid grid-cols-12 items-center gap-2 sm:gap-3"
            data-testid={`demo-country-row-${r.iso || "unknown"}`}>
            <div className="col-span-1 text-right font-mono text-xs" style={{ color: TH.textDim }}>
              {idx + 1}
            </div>
            <div className="col-span-1 text-2xl text-center leading-none">{r.flag}</div>
            <div className="col-span-4 sm:col-span-3 text-sm truncate" style={{ color: TH.text }}>
              {r.country}
            </div>
            <div className="col-span-5 sm:col-span-6 h-2 rounded-full overflow-hidden" style={{ backgroundColor: TH.surfaceHi }}>
              <div className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.max(3, (r.users / maxUsers) * 100)}%`,
                  background: r.country === "Unknown"
                    ? TH.textDim
                    : `linear-gradient(90deg, ${TH.accent}, ${TH.pop})`,
                  boxShadow: r.country === "Unknown" ? "none" : `0 0 14px ${TH.accent}77`,
                }} />
            </div>
            <div className="col-span-1 text-right font-mono text-sm font-bold" style={{ color: TH.text }}>
              {r.users}
            </div>
            <div className="hidden sm:block col-span-1 text-right font-mono text-xs" style={{ color: TH.textMute }}>
              {r.pct}%
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Timeline = ({ rows }) => {
  const max = Math.max(...rows.map((r) => r.active));
  return (
    <div className="rounded-xl p-5" style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}` }}
      data-testid="demo-timeline">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] mb-4" style={{ color: TH.textMute }}>
        Daily active users · Last 30 days
      </div>
      <div className="flex items-end gap-1 sm:gap-1.5 h-32">
        {rows.map((r) => {
          const h = max ? Math.max(2, (r.active / max) * 100) : 2;
          return (
            <div key={r.date} className="flex-1 group relative">
              <div
                className="w-full rounded-t-sm transition-all cursor-pointer hover:opacity-100"
                style={{
                  height: `${h}%`,
                  background: `linear-gradient(180deg, ${TH.pop}, ${TH.accent})`,
                  opacity: 0.78,
                }}
                title={`${r.date} · ${r.active} active visitor${r.active === 1 ? "" : "s"}`}
              />
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md
                opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 text-[10px] font-mono whitespace-nowrap"
                style={{ backgroundColor: TH.bg, border: `1px solid ${TH.borderHi}`, color: TH.text }}>
                <div className="font-bold">{r.date}</div>
                <div>{r.active} active</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between font-mono text-[9px]" style={{ color: TH.textDim }}>
        <span>{rows[0]?.date}</span>
        <span>{rows[rows.length - 1]?.date}</span>
      </div>
    </div>
  );
};
